import { type FC } from 'react';
import { Divider, Group, SegmentedControl } from '@mantine/core';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { $payinBalance, getPayinBalanceFx } from '~/entities/payin';
import { $payoutBalance, getPayoutBalanceFx } from '~/entities/payout';
import { $processingMode, isProcessingMode, setProcessingMode } from '~/entities/processing-mode';
import { Currency } from '~/entities/currency';
import { formatAmount } from '~/shared/lib/utils/formatAmount';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { routes } from '~/shared/constants/routes';
import { prepareBalance } from './lib';
import { processingModes } from './constants';
import { Balances } from './components';
import styles from './Balance.module.css';

export const Balance: FC = () => {
    const isMobile = useBreakpoint('mobile');
    const navigate = useNavigate();

    const { mode, updateMode, payinBalance, payoutBalance, isPayinBalanceLoading, isPayoutBalanceLoading } = useUnit({
        mode: $processingMode,
        updateMode: setProcessingMode,
        payinBalance: $payinBalance,
        payoutBalance: $payoutBalance,
        isPayinBalanceLoading: getPayinBalanceFx.pending,
        isPayoutBalanceLoading: getPayoutBalanceFx.pending,
    });

    const payinRubBalance = prepareBalance(payinBalance, Currency.CURRENCY_RUB);
    const payoutRubBalance = prepareBalance(payoutBalance, Currency.CURRENCY_RUB);

    const rootClasses = classNames(styles.root, {
        [styles.compact]: isMobile,
    });

    const balance = mode === 'payin' ? payinRubBalance : payoutRubBalance;
    const isLoading = mode === 'payin' ? isPayinBalanceLoading : isPayoutBalanceLoading;

    const available = formatAmount(balance?.value?.value);
    const hold = formatAmount(balance?.hold?.value);

    const handleChange = (value: string) => {
        if (!isProcessingMode(value)) {
            return;
        }

        updateMode(value);
        navigate(routes[value]);
    };

    return (
        <Group
            className={rootClasses}
            px={8}
            py={4}
            gap={!isMobile ? 16 : 4}
        >
            <SegmentedControl
                w={isMobile ? '100%' : 'auto'}
                size='sm'
                data={processingModes}
                value={mode}
                onChange={handleChange}
                bg='inherit'
            />

            {!isMobile && <Divider orientation='vertical' />}

            <Balances
                currency={balance?.currency}
                available={available}
                hold={hold}
                isLoading={isLoading}
            />
        </Group>
    );
};
